/**
 * Sockets: the points on a figure that gear hangs from — the upper and lower back, each hip, a
 * strap on each shoulder, the outside of each ankle. Like joints they are worked out from the
 * skeleton every frame, so a dagger at the ankle follows the stride and a sword on the back turns
 * with the body; each also knows whether it faces the camera in this view (and so whether what
 * hangs there is drawn in front of the body or behind it), and which way is "outward" on screen.
 */
import { torsoWidths } from './figure';
import type { Side, Skeleton } from './skeleton';

export const SOCKETS = [
  'backUpper',
  'backLower',
  'waistA',
  'waistB',
  'shoulderA',
  'shoulderB',
  'ankleA',
  'ankleB',
] as const;
export type SocketName = (typeof SOCKETS)[number];

export const SOCKET_LABELS: Record<SocketName, string> = {
  backUpper: 'หลังส่วนบน',
  backLower: 'หลังส่วนล่าง',
  waistA: 'เอวขวา',
  waistB: 'เอวซ้าย',
  shoulderA: 'หัวไหล่ขวา',
  shoulderB: 'หัวไหล่ซ้าย',
  ankleA: 'ข้อเท้าขวา',
  ankleB: 'ข้อเท้าซ้าย',
};

export interface Socket {
  x: number;
  y: number;
  z: number;
  /** Screen direction away from the body's middle here: -1 left, +1 right (0 dead centre). */
  out: number;
  /** The socket is on the side of the body facing away from the camera. */
  hidden: boolean;
}

/** Depth for gear: over every clothing layer when it faces the camera, behind the body when not. */
const FRONT = 0.08;
const BEHIND = -0.6;

export function socketsOf(sk: Skeleton): Record<SocketName, Socket> {
  const j = sk.joints;
  const [shw, whw] = torsoWidths(sk);
  /** Screen direction the right side (A) lies in; 0 when the sides are stacked in profile. */
  const a = Math.sign(j.shoulderA.x - j.chest.x);
  // The back is toward -x in the turned views (the figure faces +x), hidden unless turned away.
  const backX = sk.square ? 0 : -(sk.view === 'side' ? shw : shw - 1);
  const backZ = sk.back ? FRONT : BEHIND;
  const side = (s: Side): number => (s === 'A' ? a : -a);
  /** A side's socket faces the camera head-on, or when it is the near side of a turned figure. */
  const near = (s: Side) => sk.square || j[`shoulder${s}`].z > 0;
  const sock = (x: number, y: number, z: number, out: number, hidden: boolean): Socket => ({
    x,
    y,
    z,
    out,
    hidden,
  });
  const waist = (s: Side) =>
    sock(
      j.waist.x + side(s) * (whw + 1.5) + (sk.view === 'side' ? 1 : 0),
      j.waist.y + 1,
      near(s) ? FRONT : BEHIND,
      side(s) || 1,
      !near(s),
    );
  const strap = (s: Side) => {
    const sh = j[`shoulder${s}`];
    return sock(
      j.chest.x + (sh.x - j.chest.x) * 0.55,
      j.chest.y + 2,
      sk.back ? BEHIND : near(s) ? FRONT : BEHIND,
      side(s) || 1,
      sk.back || !near(s),
    );
  };
  const ankle = (s: Side) => {
    const an = j[`ankle${s}`];
    const out = side(s) || 1;
    return sock(an.x + out * 2, an.y - 3, j[`hip${s}`].z + 0.04, out, false);
  };
  return {
    backUpper: sock(j.chest.x + backX, j.chest.y + 4, backZ, -1, !sk.back),
    backLower: sock(j.waist.x + backX, j.waist.y + 1, backZ, -1, !sk.back),
    waistA: waist('A'),
    waistB: waist('B'),
    shoulderA: strap('A'),
    shoulderB: strap('B'),
    ankleA: ankle('A'),
    ankleB: ankle('B'),
  };
}
